import React, { useCallback, useEffect, useState } from 'react';

import { Screen, LoadingContainer } from '@sopranodesign/pulse-components';
import {
  usePulse,
  useQueryClient,
  useQuery,
  useCurrentTimeZone,
  useMutation
} from '@sopranodesign/pulse-hooks';

import { AuthContext } from '../Context/index';
import {
  hasPermission as hasPermissionCallback,
  hasSomePermissions as hasSomePermissionsCallback,
  hasAllPermissions as hasAllPermissionsCallback,
  hasGlobalRole as hasGlobalRoleCallback,
  hasSomeGlobalRoles as hasSomeGlobalRolesCallback,
  hasAllGlobalRoles as hasAllGlobalRolesCallback,
  hasFeature as hasFeatureCallback,
  hasActiveFeatureGate as hasActiveFeatureGateCallback
} from '../callbacks/index';
import { CLIENT_ID_SESSION_KEY } from '../constants';
import { setTimezone } from '../api';

const defaultAuth = {
  user: null,
  client: null
};

const AuthProvider = ({ children }) => {
  const pulse = usePulse();

  const [auth, setAuth] = useState(
    localStorage.getItem('token') ? null : defaultAuth
  );

  const mutation = useMutation({
    mutationFn: setTimezone
  });

  const currentTimezone = useCurrentTimeZone();

  const canUpdateOwnTimezone = hasPermissionCallback('view users')(auth);

  const queryClient = useQueryClient();

  const {
    data: dataAuthCheck,
    isError,
    isSuccess,
    refetch: refetchAuth
  } = useQuery({
    queryKey: [
      'auth/check'
    ],
    enabled: !auth
  });

  useEffect(() => {
    if (isError) {
      localStorage.removeItem('token');
      sessionStorage.clear();

      setAuth(defaultAuth);

      queryClient.resetQueries();
    }
  }, [currentTimezone, isError, queryClient]);

  useEffect(() => {
    if (isSuccess) {
      const { client, ...user } = dataAuthCheck.data;

      sessionStorage.clear();
      sessionStorage.setItem(CLIENT_ID_SESSION_KEY, client.id);
      setAuth({ user, client });

      if (
        canUpdateOwnTimezone
        && (
          !user.timezone
          || user.timezone !== currentTimezone
        )
      ) {
        mutation.mutate({
          id: user.id,
          timezone: currentTimezone
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, dataAuthCheck]);

  const loginUser = useCallback(({ token, user, client }) => {
    localStorage.setItem('token', token);
    sessionStorage.clear();
    sessionStorage.setItem(CLIENT_ID_SESSION_KEY, client.id);
    setAuth({ user, client });
  }, []);

  const logoutUser = useCallback(
    async redirect => {
      await queryClient.resetQueries();
      sessionStorage.clear();
      localStorage.removeItem('token');
      setAuth(defaultAuth);

      if (redirect) {
        window.location.replace(redirect);
      }
    },
    [queryClient]
  );

  const hasPermission = useCallback(
    permission => {
      return hasPermissionCallback(permission)(auth);
    },
    [auth]
  );

  const hasActiveFeatureGate = useCallback(
    (...featureGate) => {
      return hasActiveFeatureGateCallback(...featureGate)(auth);
    },
    [auth]
  );

  const hasAllPermissions = useCallback(
    (...permissions) => {
      return hasAllPermissionsCallback(...permissions)(auth);
    },
    [auth]
  );

  const hasSomePermissions = useCallback(
    (...permissions) => {
      return hasSomePermissionsCallback(...permissions)(auth);
    },
    [auth]
  );

  const hasGlobalRole = useCallback(
    role => {
      return hasGlobalRoleCallback(role)(auth);
    },
    [auth]
  );

  const hasAllGlobalRoles = useCallback(
    (...roles) => {
      return hasAllGlobalRolesCallback(...roles)(auth);
    },
    [auth]
  );

  const hasSomeGlobalRoles = useCallback(
    (...roles) => {
      return hasSomeGlobalRolesCallback(...roles)(auth);
    },
    [auth]
  );

  const hasFeature = useCallback(
    feature => {
      return hasFeatureCallback(feature)(auth);
    },
    [auth]
  );

  if (!auth) {
    return (
      <Screen
        flex="true"
        center="true"
      >
        <LoadingContainer />
      </Screen>
    );
  }

  const authContext = {
    // Details
    authenticated: Boolean(auth.user),
    // User
    ...auth,
    setAuth,
    // Roles and permissions
    hasPermission,
    hasAllPermissions,
    hasSomePermissions,
    hasGlobalRole,
    hasAllGlobalRoles,
    hasSomeGlobalRoles,
    // Features
    hasFeature,
    // Feature Gates
    hasActiveFeatureGate,
    // Callbacks
    loginUser,
    logoutUser,
    refetchAuth,
    // Pulse
    owner: pulse.owner,
    platform: pulse.platform
  };

  return (
    <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
export { defaultAuth };
